import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from "@azure/msal-angular";
import { EventMessage, EventType, InteractionStatus, RedirectRequest } from "@azure/msal-browser";
import { BehaviorSubject, filter, firstValueFrom, map, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LoginStatus, SubscriptionType } from "src/app/models/Enums";
import { environment } from "src/environments/environment";

import ApiVersionConstants from "../constants/api-version-constants";
import RouteNameConstants from "../constants/route-name-constants";
import { UserProfile } from "../models/UserProfile";
import { StorageService } from "./storage.service";

@Injectable({
  providedIn: "root",
})
export class AuthService implements OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  private baseUrl = `${environment.apiConfig.uri}/${ApiVersionConstants.V_2}/auth`;
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  private isUserProfileSetSubject = new BehaviorSubject<LoginStatus>(LoginStatus.LoginInProgress);
  private userProfileSubject = new BehaviorSubject<UserProfile>(undefined);

  public isLoggedIn: boolean = false;
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();
  public isUserProfileSet$ = this.isUserProfileSetSubject.asObservable();
  public userProfile$ = this.userProfileSubject.asObservable();
  public isUserProfileSet = false;
  public user: UserProfile;

  constructor(
    private msalService: MsalService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalBroadcastService: MsalBroadcastService,
    private http: HttpClient,
    private storage: StorageService,
    private router: Router,
  ) {
    this.user = this.storage.getUser();
    this.setMsalInitialization();
    this.checkAndSetActiveAccount();
    this.setUserProfile();
  }

  private setMsalInitialization(): void {
    // Checks for events emitted when a user logs in or out of another tab or window
    this.msalService.instance.enableAccountStorageEvents();

    // this.setLoginDisplay();
    this.msalBroadcastService.msalSubject$.subscribe((msg: EventMessage) => {
      switch (msg.eventType) {
        case EventType.ACCOUNT_ADDED:
        case EventType.ACCOUNT_REMOVED:
          this.setUserProfile();
          if (this.isLoggedIn) {
            // update that user signed-in
            this.updateIsLoggedIn();
          } else {
            // account logged out in a different tab, redirect to homepage
            this.router.navigate([RouteNameConstants.LOGIN]);
          }
          break;

        case EventType.LOGIN_SUCCESS:
          this.setUserProfile();
          break;

        case EventType.ACQUIRE_TOKEN_FAILURE:
          this.login();
          break;

        default:
          break;
      }
    });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  private updateIsLoggedIn(): void {
    this.isLoggedIn = this.msalService.instance.getAllAccounts().length > 0;
    this.isLoggedInSubject.next(this.isLoggedIn);
  }

  private checkAndSetActiveAccount(): void {
    this.updateIsLoggedIn();

    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.isLoggedIn) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  public async setUserProfile(): Promise<void> {
    if (this.isLoggedIn === false) {
      return;
    }

    const accountInfo = this.msalService.instance.getActiveAccount();
    // if we already have account don't retrieve profile from backend!
    if (this.user && this.user.azureObjectId === accountInfo?.localAccountId) {
      this.onUserProfileSet();
      return;
    }

    const user = await firstValueFrom(this.retrieveUserProfile());
    this.onUserProfileRetrieve(user);
  }

  private retrieveUserProfile(): Observable<UserProfile> {
    return this.http
      .get<UserProfile>(`${this.baseUrl}/userProfile`)
      .pipe(map((x) => new UserProfile().deserialize(x)));
  }

  public async isAdminGroupProfileAsync(): Promise<boolean> {
    if (!this.user) {
      await this.setUserProfile();
    }
    return this.user.isAdminGroupProfile;
  }

  public async isCountryAdminAsync(): Promise<boolean> {
    if (!this.user) {
      await this.setUserProfile();
    }
    return this.user.isCountryAdmin;
  }

  public async hasSubscriptionAsync(subscription: SubscriptionType): Promise<boolean> {
    if (!this.user) {
      await this.setUserProfile();
    }

    switch (subscription) {
      case SubscriptionType.Reminders:
        return this.user.allowReminders;

      case SubscriptionType.ServiceRequest:
        return this.user.allowServiceRequest;

      case SubscriptionType.PurchaseOrder:
        return this.user.allowPurchaseOrder;

      case SubscriptionType.Contracts:
        return this.user.allowContracts;

      case SubscriptionType.TechCover:
        return this.user.allowTechCover;

      default:
        return this.hasSubscription(subscription);
    }
  }

  public onUserProfileRetrieve(user: UserProfile) {
    this.user = user;
    this.storage.setUser(user);
    this.onUserProfileSet();
  }

  private onUserProfileSet(): void {
    this.isUserProfileSetSubject.next(LoginStatus.LoggedIn);
    this.userProfileSubject.next(this.user);
    this.isUserProfileSet = true;
  }

  public login(url?: string): void {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
        redirectStartPage: url,
      } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

  public async logout() {
    if (!this.isLoggedIn) {
      return;
    }

    this.storage.clearUserProfileStorage();
    this.isUserProfileSetSubject.next(LoginStatus.LoggedOut);
    this.isLoggedInSubject.next(false);
    this.userProfileSubject.next(null);

    try {
      await firstValueFrom(this.http.post(`${this.baseUrl}/logout`, null));
    } finally {
      this.msalService.logoutRedirect();
    }
  }

  public hasSubscription(subscription: SubscriptionType) {
    return this.user?.subscriptions.some((x) => x === subscription);
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
